import { getIn } from 'final-form';
import { v4 as uuidv4 } from 'uuid';

import { IDictionaryParticipantPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryParticipantPositionSelectOption';
import { IDictionaryPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { IKnmErknmFormEventInspector } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEventInspector';
import { IKnmErknmFormInspector } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormInspector';
import { ISignersForm } from 'KnmErknmPackage/MainModule/interfaces/ISignersForm';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { IKnmActSingerSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmActSingerSelectOption';

export type ComparablePosition =
  | IDictionaryParticipantPositionSelectOption
  | IDictionaryPositionSelectOption
  | undefined;

/** Минимальный API формы для синхронизации подписантов ↔ knoInspectors (Decision / КНМ / ПМ). */
export type ActOfficialsFormApi = {
  change: (name: string, value?: unknown) => void;
  getState: () => { values: object };
  mutators: {
    push?: (name: string, value: unknown) => void;
    remove?: (name: string, index: number) => void;
  };
};

export interface IOfficialIdentity {
  fullName: string | undefined;
  position: IDictionaryPositionSelectOption | IDictionaryParticipantPositionSelectOption | undefined;
}

/** Поля акта Decision (organization.act.*) — используются синком паспорта Decision. */
export const ACT_KNO_INSPECTORS_FIELD = 'organization.act.knoInspectors';
export const ACT_SIGNERS_FIELD = 'organization.act.signers';
export const ACT_FIO_AND_POSITION_SIGNER_FIELD = 'organization.act.fioAndPositionSinger';
export const ACT_FIO_SIGNER_FIELD = 'organization.act.fioSigner';
export const ACT_TITLE_SIGNER_FIELD = 'organization.act.titleSigner';

export const changeFormField = (form: ActOfficialsFormApi, fieldName: string, value: unknown): void => {
  form.change(fieldName, value);
};

export const getSignerFio = (fioSigner: ISignersForm['fioSigner']): string | undefined => {
  if (!fioSigner) {
    return undefined;
  }

  if (typeof fioSigner === 'string') {
    return fioSigner;
  }

  return String(fioSigner.value ?? fioSigner.label ?? '');
};

export const getSignerIdentity = (signer: Pick<ISignersForm, 'fioSigner' | 'titleSigner'>): IOfficialIdentity => ({
  fullName: getSignerFio(signer.fioSigner),
  position: signer.titleSigner as IDictionaryParticipantPositionSelectOption | undefined,
});

export const isSamePosition = (left: ComparablePosition, right: ComparablePosition): boolean => {
  if (!left || !right) {
    return false;
  }

  const leftVersionId = left.value?.dictVersionId;
  const rightVersionId = right.value?.dictVersionId;

  if (leftVersionId && rightVersionId) {
    return leftVersionId === rightVersionId;
  }

  const leftDictId = left.value?.dictId;
  const rightDictId = right.value?.dictId;

  if (leftDictId && rightDictId) {
    return leftDictId === rightDictId;
  }

  return Boolean(left.label && right.label && left.label === right.label);
};

export const isSameOfficial = (
  fullName: string | undefined,
  position: ComparablePosition,
  original: IOfficialIdentity,
): boolean => fullName === original.fullName && isSamePosition(position, original.position);

export const canMatchOfficial = (identity: IOfficialIdentity): boolean =>
  Boolean(identity.fullName && identity.position);

const getEventInspectorOptionText = (inspector: IKnmErknmFormEventInspector | undefined): string =>
  String(inspector?.fullName?.value ?? inspector?.fullName?.label ?? '').trim();

export const getKnoInspectorIdentityIds = (
  knoInspectors: IKnmErknmFormInspector[] | undefined,
  identity: IOfficialIdentity,
): string[] => {
  if (!canMatchOfficial(identity)) {
    return [];
  }

  return (knoInspectors || []).flatMap(item => {
    if (!isSameOfficial(item.knoInspector?.value?.fullName, item.knoInspector?.value?.position, identity)) {
      return [];
    }

    return [item.knoInspector?.id, item.knoInspector?.value?.listId, item.knoInspector?.value?.guid]
      .filter((id): id is string => Boolean(id))
      .map(String);
  });
};

/**
 * ДЛ указан в «Должностные лица, осуществившие действие» хотя бы одного события акта.
 * Такие записи нельзя снимать с knoInspectors при удалении/смене подписанта.
 */
export const isOfficialUsedInActEvents = (
  events: Pick<IKnmErknmFormEvent, 'inspectors'>[] | undefined,
  identity: IOfficialIdentity,
  knoInspectorIds: string[] = [],
): boolean => {
  const fullName = identity.fullName?.trim();

  if (!fullName && !knoInspectorIds.length) {
    return false;
  }

  const positionText = (identity.position?.label || identity.position?.title || '').trim();
  const identityIds = new Set(knoInspectorIds);

  return (events || []).some(event =>
    (event.inspectors || []).some(inspector => {
      const optionId = inspector.fullName?.id;

      if (optionId != null && identityIds.has(String(optionId))) {
        return true;
      }

      const optionText = getEventInspectorOptionText(inspector);

      if (!fullName || !optionText) {
        return false;
      }

      if (optionText === fullName) {
        return true;
      }

      if (!optionText.startsWith(`${fullName} - `)) {
        return false;
      }

      return !positionText || optionText.includes(positionText);
    }),
  );
};

export const buildKnoInspectorOption = (
  inspector: IKnmErknmFormInspector,
  previousOption?: IKnmActSingerSelectOption,
): IKnmActSingerSelectOption => ({
  id: previousOption?.id || inspector.guid || inspector.listId || '',
  label: `${inspector.fullName || ''} - ${inspector.position?.label || ''}`,
  value: inspector,
});

export const buildSignerFioOption = (
  fullName: string | undefined,
  previousFio: ISignersForm['fioSigner'],
): ISelectOption | string | undefined => {
  if (!fullName) {
    return undefined;
  }

  if (typeof previousFio === 'object' && previousFio) {
    return {
      ...previousFio,
      label: fullName,
      value: fullName,
    };
  }

  if (typeof previousFio === 'string') {
    return fullName;
  }

  return {
    id: '',
    label: fullName,
    value: fullName,
  };
};

export const removeMatchingFieldArrayItems = (
  form: ActOfficialsFormApi,
  fieldName: string,
  shouldRemove: (item: unknown) => boolean,
): void => {
  const items = getIn(form.getState().values, fieldName);

  const remove = form.mutators?.remove;

  if (!Array.isArray(items) || !remove) {
    return;
  }

  items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => shouldRemove(item))
    .map(({ index }) => index)
    .sort((left, right) => right - left)
    .forEach(index => {
      remove(fieldName, index);
    });
};

export const buildInspectorFromIdentity = (
  identity: IOfficialIdentity,
  passportInspectors: IKnmErknmFormInspector[] | undefined,
  previous?: IKnmErknmFormInspector,
): IKnmErknmFormInspector => {
  // Сопоставление только по паре ФИО+должность: одно и то же ФИО с разными должностями — разные ДЛ.
  const passportMatch = canMatchOfficial(identity)
    ? passportInspectors?.find(inspector => isSameOfficial(inspector.fullName, inspector.position, identity))
    : undefined;

  if (passportMatch) {
    return {
      fullName: passportMatch.fullName,
      position: identity.position ?? passportMatch.position,
      listId: passportMatch.listId,
      guid: passportMatch.guid,
      dateCreate: passportMatch.dateCreate,
    };
  }

  return {
    fullName: identity.fullName,
    position: identity.position,
    listId: previous?.listId || uuidv4(),
    guid: previous?.guid,
    dateCreate: previous?.dateCreate,
  };
};

/** Строка *.knoInspectors — структура как при загрузке акта из API. */
export const buildKnoInspectorRow = (
  identity: IOfficialIdentity,
  passportInspectors: IKnmErknmFormInspector[] | undefined,
): IKnmErknmFormInspector => {
  const inspectorValue = buildInspectorFromIdentity(identity, passportInspectors);
  const listId = inspectorValue.listId || uuidv4();
  const guid = inspectorValue.guid || listId;

  const knoInspectorValue: IKnmErknmFormInspector = {
    fullName: inspectorValue.fullName,
    position: inspectorValue.position,
    listId,
    guid,
    dateCreate: inspectorValue.dateCreate,
  };

  return {
    listId,
    guid,
    dateCreate: inspectorValue.dateCreate,
    knoInspector: buildKnoInspectorOption(knoInspectorValue),
    fullName: undefined,
    position: undefined,
  };
};
